import type { SidebarItemType } from '@/models/app';
import { PartnerCodeName } from '@/models/partner';
import { deliveryTabRoles } from '@/constants/deliveryOrder';
import { qaCalibrationRoles } from '@/constants/qaCalibration';

export const familyWidgetList: SidebarItemType[] = [
  {
    name: 'Ride Ordering',
    icon: 'fa fa-car',
    path: '/family/ride-ordering',
  },
  {
    name: 'Profile',
    icon: 'fa fa-user-circle-o',
    path: '/family/profile',
    description: 'View or Update your personal information.',
  },
  {
    name: 'Rides Overview',
    icon: 'fa fa-car',
    path: '/family/ride-ordering',
    subItemList: [
      { text: 'Schedule a future ride', path: '/family/ride-ordering?rideFormType=schedule' },
      { text: 'View your scheduled rides', path: '/family/scheduled-rides' },
      { text: 'Live ride map', path: '/family/live-ride-map' },
    ],
    children: [
      {
        name: 'Scheduled Ride Calendar',
        path: '/family/scheduled-rides',
      },
      {
        name: 'Live Ride Map',
        path: '/family/live-ride-map',
      },
    ],
  },
  {
    name: 'Grocery',
    icon: 'fa fa-shopping-cart',
    path: '/family/grocery',
    children: [
      {
        name: 'Grocery Lists',
        path: '/family/grocery-lists',
      },
    ],
  },
  {
    name: 'Membership Plan',
    icon: 'fa fa-address-card',
    path: '/family/membership-plan',
    description: 'View your current membership and compare membership plans',
  },
  {
    name: 'Payment & Receipts',
    icon: 'fa fa-money',
    path: '/family/payment-methods',
    subItemList: [
      { text: 'View your payment methods on file', path: '/family/payment-methods' },
      { text: 'Update your payment information', path: '/family/payment-methods' },
      { text: 'Add GoGo credits to your account', path: '/family/ride-credits' },
      { text: 'Review your receipts', path: '/family/receipts' },
    ],
    children: [
      {
        name: 'Credit / Debit Cards',
        path: '/family/payment-methods',
      },
      {
        name: 'GoGo Credits',
        path: '/family/ride-credits',
      },
      {
        name: 'Receipts',
        path: '/family/receipts',
      },
    ],
  },
  {
    name: 'Order History',
    icon: 'fa fa-history',
    path: '/family/ride-history',
    subItemList: [
      { text: 'View ride, pharmacy and special request history', path: '/family/ride-history' },
      { text: 'View grocery history', path: '/family/grocery-history' },
      { text: 'View gourmet history', path: '/family/gourmet-history' },
    ],
    children: [
      {
        name: 'Rides',
        path: '/family/ride-history',
      },
      {
        name: 'Grocery',
        path: '/family/grocery-history',
      },
      {
        name: 'Gourmet',
        path: '/family/gourmet-history',
      },
    ],
  },
  {
    name: 'Settings',
    icon: 'fa fa-heart-o',
    path: '/family/family-members',
    subItemList: [
      { text: 'Add/review family member contacts', path: '/family/family-members' },
      { text: 'Add/review your custom locations', path: '/family/custom-locations' },
      { text: 'Set/review your active hours', path: '/family/active-hours' },
      { text: 'Home services requests', path: '/family/home-services-requests' },
    ],
    children: [
      {
        name: 'Family Contacts',
        path: '/family/family-members',
      },
      {
        name: 'Custom Locations',
        path: '/family/custom-locations',
      },
      {
        name: 'Active Hours',
        path: '/family/active-hours',
      },
      {
        name: 'Home Services Requests',
        path: '/family/home-services-requests',
      },
    ],
  },
  {
    name: 'Knowledge Base',
    icon: 'fa fa-book',
    path: '/family/ride-history',
    subItemList: [
      { text: 'How to documents', path: '/family/how-to-document' },
      { text: 'FAQ - About GoGo', path: '/family/about-gogo' },
    ],
    children: [
      {
        name: 'How to documents',
        path: '/family/how-to-document',
      },
      {
        name: 'FAQ - About GoGo',
        path: '/family/about-gogo',
      },
    ],
  },
  {
    name: 'Contact Us',
    icon: 'fa fa-address-book',
    path: '/family/contact-us',
    description: 'Send us a message & view other ways to reach us. ',
  },
  {
    name: 'Security',
    icon: 'fa fa-shield',
    path: '/family/2fa',
    children: [
      {
        name: 'Two-Factor Authentication',
        path: '/family/2fa',
      },
    ],
  },
];

export const partnerWidgetList: SidebarItemType[] = [
  {
    name: 'Dashboard Overview',
    icon: 'fa fa-dashboard',
    isOnlyMenu: true,
    path: '/partner/overview',
    partnersToExclude: [],
  },
  {
    name: 'Registrations',
    icon: 'fa fa-registered',
    path: '/partner/registration',
    partnersToExclude: [],
    children: [
      {
        name: 'Registration',
        path: '/partner/registration',
      },
      {
        name: 'Registration by CSV',
        path: '/partner/registration-csv',
      },
      {
        name: 'Add Member Manually',
        path: '/partner/add-member-manually',
      },
    ],
  },
  {
    name: 'User Search',
    icon: 'fa fa-user',
    path: '/partner/user-search',
    partnersToExclude: [],
  },
  {
    name: 'Ride History',
    icon: 'fa fa-search',
    path: '/partner/ride-history',
    partnersToExclude: [],
  },
  {
    name: 'Ride Ordering',
    icon: 'fa fa-car',
    roles: ['premium'],
    path: '/partner/ride-ordering',
    partnersToExclude: [],
  },
  {
    name: 'Scheduled Rides',
    icon: 'fa fa-calendar',
    path: '/partner/scheduled-rides',
    partnersToExclude: [],
  },
  {
    name: 'Will Call Rides',
    icon: 'fa fa-phone',
    path: '/partner/willcall-rides',
    partnersToExclude: [],
  },
  {
    name: 'Call Logs',
    icon: 'fa fa-history',
    roles: ['sanleandro', 'hamv', 'pace', 'fresno'],
    path: '/partner/call-logs',
    partnersToExclude: [],
  },
  {
    name: 'Gourmet',
    icon: 'fa fa-cutlery',
    path: '/partner/delivery-search',
    roles: ['Immanuel'],
    partnersToExclude: [],
    children: [
      {
        name: 'Search',
        path: '/partner/delivery-search',
        partnersToExclude: [],
      },
      {
        name: 'Orders',
        path: '/partner/delivery-orders',
        partnersToExclude: [],
      },
    ],
  },
  {
    name: 'Custom Locations',
    icon: 'fa fa-address-book',
    path: '/partner/custom-locations',
    partnersToExclude: [],
  },
  {
    name: 'Payment Methods',
    icon: 'fa fa-money',
    path: '/partner/payment-methods',
    partnersToExclude: [PartnerCodeName.CVRC, PartnerCodeName.ACRC, PartnerCodeName.ORIONISO, PartnerCodeName.ACEFMS],
    children: [
      {
        name: 'Credit / Debit Cards',
        path: '/partner/payment-methods',
      },
      {
        name: 'GoGo Credits',
        path: '/partner/ride-credits',
      },
    ],
  },
  {
    name: 'Service Check',
    icon: 'fa fa-dollar',
    path: '/partner/service-check',
    partnersToExclude: [PartnerCodeName.CVRC, PartnerCodeName.ACRC, PartnerCodeName.ORIONISO, PartnerCodeName.ACEFMS],
  },
  {
    name: 'Receipts',
    icon: 'fa fa-copy',
    path: '/partner/receipts',
    partnersToExclude: [PartnerCodeName.CVRC, PartnerCodeName.ACRC, PartnerCodeName.ORIONISO, PartnerCodeName.ACEFMS],
  },
  {
    name: 'Marketing Materials',
    icon: 'fa fa-line-chart',
    path: '/partner/marketing-marterials',
    partnersToExclude: [PartnerCodeName.CVRC, PartnerCodeName.ACRC, PartnerCodeName.ORIONISO, PartnerCodeName.ACEFMS],
  },
  {
    name: 'Comm Preferences',
    icon: 'fa fa-envelope',
    path: '/partner/partner-communication-preferences',
    partnersToExclude: [PartnerCodeName.CVRC, PartnerCodeName.ACRC, PartnerCodeName.ORIONISO, PartnerCodeName.ACEFMS],
  },
  {
    name: 'Call Stats',
    icon: 'fa fa-bar-chart',
    path: '/partner/call-stats',
  },
  {
    name: 'Security',
    icon: 'fa fa-shield',
    path: '/partner/2fa',
    children: [
      {
        name: 'Two-Factor Authentication',
        path: '/partner/2fa',
      },
    ],
  },
];

export const operatorWidgetList: SidebarItemType[] = [
  {
    name: 'Dashboard Overview',
    icon: 'fa fa-dashboard',
    isOnlyMenu: true,
    path: '/operator/overview',
  },
  {
    name: 'Live Calls',
    icon: 'fa fa-volume-control-phone',
    path: '/operator/live-calls',
    description:
      'In this section, you can view the current live calls that our team members are on. You can also view if you are having connection issues.',
  },
  {
    name: 'Rides',
    icon: 'fa fa-car',
    path: '/operator/ride-ordering',
    children: [
      {
        name: 'Ride Ordering',
        path: '/operator/ride-ordering',
      },
      {
        name: 'Ride History',
        path: '/operator/ride-history',
      },
      {
        name: 'Scheduled Rides',
        path: '/operator/scheduled-rides',
      },
      {
        name: 'Will Call Rides',
        path: '/operator/willcall-rides',
      },
      {
        name: 'Live Rides',
        path: '/operator/live-ride',
      },
      {
        name: 'Ride Monitoring',
        path: '/operator/ride-monitoring',
      },
      {
        name: 'Receipts',
        path: '/operator/receipts',
      },
      {
        name: 'SMS',
        path: '/operator/sms-board',
        roles: [
          'admin',
          'director',
          'supervisor',
          'lossPrevention',
          'lossPreventionGourmet',
          'lossPreventionGrocery',
          'research-operator',
          'homeServices-operator',
          'gogo-welcome-operator',
          'facebookLeads-registrations-operator',
        ],
      },
    ],
    subItemList: [
      { text: 'Ride Ordering', path: '/operator/ride-ordering' },
      { text: 'Ride History', path: '/operator/ride-history' },
      { text: 'Scheduled Rides', path: '/operator/scheduled-rides' },
      { text: 'Will Call Rides', path: '/operator/willcall-rides' },
      { text: 'Live Rides', path: '/operator/live-ride' },
      { text: 'Ride Monitoring', path: '/operator/ride-monitoring' },
      { text: 'Receipts', path: '/operator/receipts' },
      { text: 'SMS', path: '/operator/sms-board' },
    ],
  },
  {
    name: 'Ride Safety & Support',
    icon: 'fa fa-headphones',
    path: '/operator/live-alerts',
    roles: ['admin', 'director', 'teamLead', 'supervisor', 'onboarding-team-lead', 'rideTeamLead', 'lossPrevention'],
    children: [
      {
        name: 'Live Alerts',
        path: '/operator/live-alerts',
        roles: ['admin', 'director', 'teamLead', 'lossPrevention'],
      },
      {
        name: 'Operator Tasks',
        path: '/operator/operator-tasks',
      },
    ],
    subItemList: [
      { text: 'Live Alerts', path: '/operator/live-alerts' },
      { text: 'Operator Tasks', path: '/operator/operator-tasks' },
    ],
  },
  {
    name: 'Client Checks',
    icon: 'fa fa-user',
    path: '/operator/fraud-check',
    children: [
      {
        name: 'Fraud Check',
        path: '/operator/fraud-check',
      },
      {
        name: 'Blacklist',
        path: '/operator/blacklist',
      },
      {
        name: 'User Interactions',
        path: '/operator/user-interactions',
      },
      {
        name: 'User Communication Preferences',
        path: '/operator/communication-preferences',
      },
      {
        name: 'User Search',
        path: '/operator/user-search',
      },
      {
        name: 'Mailings Search',
        path: '/operator/mailings-search',
      },
      {
        name: 'Email Search',
        path: '/operator/email-search',
      },
      {
        name: 'SMS Search',
        path: '/operator/sms-search',
      },
      {
        name: 'Call Logs',
        path: '/operator/call-logs',
      },
      {
        name: 'Bulk Transcribe Call Logs',
        path: '/operator/bulk-transcribe-call-logs',
        roles: ['admin', 'director'],
      },
      {
        name: 'QA Calibrations',
        path: '/operator/qa-calibrations',
        roles: qaCalibrationRoles,
      },
      {
        name: 'User Plan',
        path: '/operator/user-plan',
      },
      {
        name: 'Referral Search',
        path: '/operator/referral-search',
      },
    ],
    subItemList: [
      { text: 'Fraud Check', path: '/operator/fraud-check' },
      { text: 'Blacklist', path: '/operator/blacklist' },
      { text: 'User Interactions', path: '/operator/user-interactions' },
      { text: 'User Communication Preferences', path: '/operator/communication-preferences' },
      { text: 'User Search', path: '/operator/user-search' },
      { text: 'Mailings Search', path: '/operator/mailings-search' },
      { text: 'Email Search', path: '/operator/email-search' },
      { text: 'SMS Search', path: '/operator/sms-search' },
      { text: 'Call Logs', path: '/operator/call-logs' },
      { text: 'Bulk Transcribe Call Logs', path: '/operator/bulk-transcribe-call-logs', roles: ['admin', 'director'] },
      { text: 'QA Calibrations', path: '/operator/qa-calibrations', roles: qaCalibrationRoles },
      { text: 'User Plan', path: '/operator/user-plan' },
    ],
  },
  {
    name: 'Payments & Credits',
    icon: 'fa fa-money',
    path: '/operator/payments',
    children: [
      {
        name: 'Payments',
        path: '/operator/payments',
      },
      {
        name: 'GoGo Credits',
        path: '/operator/ride-credits',
      },
      {
        name: 'Checks',
        path: '/operator/checks',
        roles: ['research-admin'],
      },
    ],
    subItemList: [
      { text: 'Payments', path: '/operator/payments' },
      { text: 'GoGo Credits', path: '/operator/ride-credits' },
      { text: 'Checks', path: '/operator/checks', roles: ['research-admin'] },
    ],
  },
  {
    name: 'Need Love',
    icon: 'fa fa-heart',
    path: '/operator/need-love',
    children: [
      {
        name: 'Need Love',
        roles: [
          'admin',
          'director',
          'supervisor',
          'operator',
          'teamLead',
          'lossPrevention',
          'qualityAssurance',
          'gourmet-operator',
          'grocery-operator',
          'rideTeamLead',
          'grocery-manager',
          'gourmet-supervisor',
        ],
        path: '/operator/need-love',
      },
      {
        name: 'Reset Password',
        path: '/operator/customer-reset-password',
      },
    ],
    subItemList: [
      { text: 'Need Love', path: '/operator/need-love' },
      { text: 'Reset Password', path: '/operator/customer-reset-password' },
    ],
  },
  {
    name: 'Settings',
    icon: 'fa fa-cog',
    path: '/operator/custom-locations',
    children: [
      {
        name: 'Custom Locations',
        path: '/operator/custom-locations',
      },
      {
        name: 'Family Member',
        path: '/operator/family-member',
      },
    ],
    subItemList: [
      { text: 'Custom Locations', path: '/operator/custom-locations' },
      { text: 'Family Member', path: '/operator/family-member' },
    ],
  },
  {
    name: 'Partners',
    icon: 'fa fa-users',
    path: '/operator/partners',
    description: 'In this section, you can view our different partner information.',
  },
  {
    name: 'Affiliate',
    icon: 'fa fa-handshake-o',
    path: '/operator/affiliate',
    description: 'In this section, you can view our different affiliate information.',
    roles: ['admin', 'affiliate-manager'],
  },
  {
    name: 'Marketing',
    icon: 'fa fa-line-chart',
    path: '/operator/marketing',
    description: 'In this section, you can submit marketing inquiries, send marketing emails and look at the GoGo mailings.',
  },
  {
    name: 'Deliveries',
    icon: 'fa fa-truck',
    path: '/operator/live-deliveries',
    description: 'Unified view for grocery and gourmet deliveries.',
    children: [
      {
        name: 'Live Orders',
        path: '/operator/live-deliveries',
        roles: deliveryTabRoles,
      },
      {
        name: 'Order History',
        path: '/operator/delivery-order-history',
        roles: deliveryTabRoles,
      },
    ],
    subItemList: [
      {
        text: 'Live Orders',
        path: '/operator/live-deliveries',
        roles: deliveryTabRoles,
      },
      {
        text: 'Order History',
        path: '/operator/delivery-order-history',
        roles: deliveryTabRoles,
      },
    ],
  },
  {
    name: 'Grocery',
    icon: 'fa fa-shopping-cart',
    path: '/operator/grocery',
    children: [
      {
        name: 'Grocery',
        path: '/operator/grocery',
      },
      {
        name: 'Registration',
        path: '/operator/grocery/registration',
        roles: [
          'admin',
          'director',
          'supervisor',
          'grocery-operator',
          'grocery-manager',
          'lossPreventionGrocery',
          'virtualAssistanceGrocery',
          'gourmet-operator',
        ],
      },
      {
        name: 'Live Groceries',
        path: '/operator/live-grocery',
        roles: [
          'admin',
          'director',
          'supervisor',
          'grocery-operator',
          'grocery-manager',
          'lossPreventionGrocery',
          'virtualAssistanceGrocery',
          'gourmet-operator',
        ],
      },
      {
        name: 'Saved Grocery',
        path: '/operator/saved-grocery',
        roles: ['admin', 'director', 'supervisor', 'grocery-manager', 'grocery-operator', 'gourmet-operator'],
      },
      {
        name: 'Scheduled Grocery',
        path: '/operator/scheduled-grocery',
        roles: ['admin', 'director', 'supervisor', 'grocery-manager', 'grocery-operator', 'gourmet-operator'],
      },
      {
        name: 'Grocery Lists',
        path: '/operator/grocery-lists',
        roles: ['admin', 'director', 'supervisor', 'grocery-manager', 'grocery-operator', 'gourmet-operator'],
      },
    ],
    subItemList: [
      { text: 'Service Check', path: '/operator/grocery#grocery-available-stores' },
      {
        text: 'Live Groceries',
        path: '/operator/live-grocery',
        roles: [
          'admin',
          'director',
          'supervisor',
          'grocery-operator',
          'grocery-manager',
          'lossPreventionGrocery',
          'virtualAssistanceGrocery',
          'gourmet-operator',
        ],
      },
      {
        text: 'Saved Grocery',
        path: '/operator/saved-grocery',
        roles: ['admin', 'director', 'supervisor', 'grocery-operator', 'grocery-manager', 'gourmet-operator'],
      },
      {
        text: 'Scheduled Grocery',
        path: '/operator/scheduled-grocery',
        roles: ['admin', 'director', 'supervisor', 'grocery-operator', 'grocery-manager', 'gourmet-operator'],
      },
      {
        text: 'Grocery Lists',
        path: '/operator/grocery-lists',
        roles: ['admin', 'director', 'supervisor', 'grocery-operator', 'grocery-manager', 'gourmet-operator'],
      },
    ],
  },
  {
    name: 'Gourmet',
    icon: 'fa fa-cutlery',
    path: '/operator/delivery-search',
    children: [
      {
        name: 'Search',
        path: '/operator/delivery-search',
      },
      {
        name: 'Manual Order',
        path: '/operator/delivery-manual-order',
        roles: ['admin', 'director', 'gourmet-supervisor', 'gourmet-operator', 'lossPreventionGourmet', 'grocery-operator'],
      },
      {
        name: 'Gourmet Estimator',
        path: '/operator/gourmet-estimator',
      },
      {
        name: 'Need Love',
        path: '/operator/delivery-need-love',
        roles: ['admin', 'gourmet-supervisor', 'gourmet-operator', 'lossPreventionGourmet', 'grocery-operator'],
      },
      {
        name: 'Communication',
        path: '/operator/delivery-sms',
        roles: [
          'admin',
          'director',
          'supervisor',
          'lossPrevention',
          'gourmet-supervisor',
          'gourmet-operator',
          'lossPreventionGourmet',
          'grocery-operator',
          'grocery-manager',
        ],
      },
      {
        name: 'Delivery Receipt',
        path: '/operator/delivery-receipt',
        roles: [
          'admin',
          'director',
          'supervisor',
          'lossPrevention',
          'gourmet-supervisor',
          'gourmet-operator',
          'lossPreventionGourmet',
          'grocery-operator',
          'rideTeamLead',
        ],
      },
      {
        name: 'Service Accounts',
        path: '/operator/service-accounts',
        roles: ['admin', 'director', 'gourmet-supervisor', 'lossPreventionGourmet'],
      },
      {
        name: 'Scheduled Delivery',
        path: '/operator/scheduled-delivery',
        roles: [
          'admin',
          'director',
          'supervisor',
          'lossPrevention',
          'gourmet-supervisor',
          'gourmet-operator',
          'lossPreventionGourmet',
          'grocery-operator',
        ],
      },
      {
        name: 'Delivery Transactions',
        path: '/operator/delivery-transactions',
        roles: ['gourmet-supervisor', 'gourmet-operator', 'lossPreventionGourmet', 'grocery-operator'],
      },
      {
        name: 'Registration',
        path: '/operator/gourmet-registration',
        roles: ['admin', 'director', 'supervisor', 'gourmet-supervisor', 'gourmet-operator'],
      },
      {
        name: 'Resume Registration',
        path: '/operator/gourmet-resume-registration',
        roles: ['admin', 'director', 'supervisor', 'gourmet-supervisor', 'gourmet-operator'],
      },
    ],
    subItemList: [
      { text: 'Search', path: '/operator/delivery-search' },
      {
        text: 'Manual Order',
        path: '/operator/delivery-manual-order',
        roles: ['admin', 'director', 'gourmet-supervisor', 'gourmet-operator', 'lossPreventionGourmet', 'grocery-operator'],
      },
      {
        text: 'Need Love',
        path: '/operator/delivery-need-love',
        roles: ['admin', 'gourmet-supervisor', 'gourmet-operator', 'lossPreventionGourmet', 'grocery-operator', 'grocery-manager'],
      },
      {
        text: 'Communication',
        path: '/operator/delivery-sms',
        roles: [
          'admin',
          'director',
          'supervisor',
          'lossPrevention',
          'gourmet-supervisor',
          'gourmet-operator',
          'lossPreventionGourmet',
          'grocery-operator',
          'grocery-manager',
        ],
      },
      {
        text: 'Receipt',
        path: '/operator/delivery-receipt',
        roles: [
          'admin',
          'director',
          'supervisor',
          'lossPrevention',
          'gourmet-supervisor',
          'gourmet-operator',
          'lossPreventionGourmet',
          'grocery-operator',
          'grocery-manager',
        ],
      },
      {
        text: 'Service Accounts',
        path: '/operator/service-accounts',
        roles: ['admin', 'director', 'gourmet-supervisor'],
      },
      {
        text: 'Scheduled Delivery',
        path: '/operator/scheduled-delivery',
        roles: [
          'admin',
          'director',
          'supervisor',
          'lossPrevention',
          'gourmet-supervisor',
          'gourmet-operator',
          'lossPreventionGourmet',
          'grocery-operator',
          'grocery-manager',
        ],
      },
      {
        text: 'Transactions',
        path: '/operator/delivery-transactions',
        roles: ['gourmet-supervisor', 'gourmet-operator', 'lossPreventionGourmet', 'grocery-operator'],
      },
      {
        text: 'Registration',
        path: '/operator/gourmet-registration',
        roles: ['admin', 'director', 'supervisor', 'gourmet-supervisor', 'gourmet-operator'],
      },
      {
        text: 'Resume Registration',
        path: '/operator/gourmet-resume-registration',
        roles: ['admin', 'director', 'supervisor', 'gourmet-supervisor', 'gourmet-operator'],
      },
    ],
  },
  {
    name: 'Home Services',
    icon: 'fa fa-home',
    path: '/operator/home-services/request',
    children: [
      {
        name: 'New Service Request',
        path: '/operator/home-services/request',
      },
      {
        name: 'Orders',
        path: '/operator/home-services/orders',
        roles: ['admin', 'director', 'supervisor', 'teamLead', 'homeServices-operator'],
      },
      {
        name: 'Providers',
        path: '/operator/home-services/providers',
        roles: ['admin', 'director', 'supervisor', 'teamLead', 'homeServices-operator'],
      },
    ],
    subItemList: [
      {
        text: 'New Service Request',
        path: '/operator/home-services/request',
      },
      {
        text: 'Orders',
        path: '/operator/home-services/orders',
        roles: ['admin', 'director', 'supervisor', 'teamLead', 'homeServices-operator'],
      },
      {
        text: 'Providers',
        path: '/operator/home-services/providers',
        roles: ['admin', 'director', 'supervisor', 'teamLead', 'homeServices-operator'],
      },
    ],
  },
  {
    name: 'Facebook',
    icon: 'fa fa-envelope',
    path: '/operator/facebook/registration',
    children: [
      {
        name: 'Registration',
        path: '/operator/facebook/registration',
      },
    ],
    subItemList: [{ text: 'Registration', path: '/operator/facebook/registration' }],
  },
  {
    name: 'Registration',
    icon: 'fa fa-registered',
    path: '/operator/registration',
    children: [
      {
        name: 'Registration',
        path: '/operator/registration',
      },
      {
        name: 'Resume Registration',
        path: '/operator/resume-registration',
      },
      {
        name: 'Service Check',
        path: '/operator/service-check',
      },
    ],
    subItemList: [
      { text: 'Registration', path: '/operator/registration' },
      { text: 'Resume Registration', path: '/operator/resume-registration' },
      { text: 'Service Check', path: '/operator/service-check' },
    ],
  },
  {
    name: 'Operator Checks',
    icon: 'fa fa-tachometer',
    path: '/operator/ping-test',
    children: [
      {
        name: 'Ping Test',
        path: '/operator/ping-test',
      },
      {
        name: 'Membership Pricing',
        path: '/operator/membership-pricing',
      },
    ],
    subItemList: [
      { text: 'Ping Test', path: '/operator/ping-test' },
      { text: 'Membership Pricing', path: '/operator/membership-pricing' },
    ],
  },
  {
    name: 'Submit Feedback',
    icon: 'fa fa-pencil-square-o',
    path: '/operator/submit-kudos-call',
    description: 'In this section you can submit a kudos call to a team lead.',
  },
  {
    name: 'AI Agents',
    icon: 'fa fa-pencil-square-o',
    path: '/operator/agent/agent',
    roles: ['admin', 'director', 'teamLead', 'qualityAssurance', 'rideTeamLead', 'grocery-manager', 'gourmet-supervisor'],
    description: 'In this section you can view and update the AI Agent settings.',
  },
  {
    name: 'Test Results',
    icon: 'fa fa-flask',
    path: '/operator/test-results',
    roles: ['admin', 'director'],
    description: 'View the latest registration test results and Playwright reports.',
  },
  {
    name: 'Management',
    icon: 'fa fa-user-md',
    path: '/operator/account',
    children: [
      {
        name: 'Management',
        roles: ['admin', 'director', 'supervisor', 'teamLead', 'lossPrevention', 'rideTeamLead', 'grocery-manager', 'gourmet-supervisor'],
        path: '/operator/management',
      },
      {
        name: 'Team Leads',
        roles: ['admin', 'director', 'supervisor', 'teamLead', 'rideTeamLead'],
        path: '/operator/team-leads',
      },
      {
        name: 'Membership',
        path: '/operator/membership',
      },
      {
        name: 'Plans',
        roles: ['admin', 'director'],
        path: '/operator/plans',
      },
      {
        name: 'Payment Tiers',
        roles: ['admin', 'director'],
        path: '/operator/payment-tiers',
      },
      {
        name: 'Calls',
        roles: ['admin', 'director', 'supervisor', 'teamLead', 'rideTeamLead', 'grocery-manager', 'gourmet-supervisor'],
        path: '/operator/calls',
      },
      {
        name: 'A/B Tests',
        roles: ['admin', 'director', 'supervisor'],
        path: '/operator/ab-tests',
      },
      {
        name: 'AI Tools',
        roles: ['admin', 'director', 'supervisor'],
        path: '/operator/ai-tools',
      },
      {
        name: 'Refunds',
        roles: ['admin', 'director', 'supervisor', 'teamLead', 'rideTeamLead', 'grocery-manager', 'gourmet-supervisor'],
        path: '/operator/refunds',
      },
      {
        name: 'Account',
        path: '/operator/account',
      },
      {
        name: 'Operators',
        roles: ['admin', 'director', 'supervisor', 'teamLead', 'rideTeamLead', 'grocery-manager', 'gourmet-supervisor'],
        path: '/operator/operators',
      },
      {
        name: 'Loss Prevention',
        roles: ['admin', 'director', 'supervisor', 'teamLead', 'rideTeamLead', 'grocery-manager', 'gourmet-supervisor'],
        path: '/operator/loss-prevention',
      },
      {
        name: 'Service Surveys',
        path: '/operator/service-surveys',
      },
      {
        name: 'Display Email Example',
        path: '/operator/display-email-example',
      },
      {
        name: 'Display SMS Example',
        path: '/operator/display-sms-example',
      },
    ],
    subItemList: [
      {
        text: 'Management',
        path: '/operator/management',
        roles: ['admin', 'director', 'supervisor', 'teamLead', 'lossPrevention', 'rideTeamLead', 'grocery-manager', 'gourmet-supervisor'],
      },
      {
        text: 'Team Leads',
        path: '/operator/team-leads',
        roles: ['admin', 'director', 'supervisor', 'teamLead', 'rideTeamLead'],
      },
      {
        text: 'Plans',
        path: '/operator/plans',
        roles: ['admin', 'director'],
      },
      {
        text: 'Payment Tiers',
        path: '/operator/payment-tiers',
        roles: ['admin', 'director'],
      },
      {
        text: 'Refunds',
        path: '/operator/refunds',
        roles: ['admin', 'director', 'supervisor', 'teamLead', 'rideTeamLead', 'grocery-manager', 'gourmet-supervisor'],
      },
      { text: 'Account', path: '/operator/account' },
      {
        text: 'Operators',
        path: '/operator/operators',
        roles: ['admin', 'director', 'supervisor', 'teamLead', 'rideTeamLead', 'grocery-manager', 'gourmet-supervisor'],
      },
      {
        text: 'Loss Prevention',
        path: '/operator/loss-prevention',
        roles: ['admin', 'director', 'supervisor', 'teamLead', 'rideTeamLead', 'grocery-manager', 'gourmet-supervisor'],
      },
      { text: 'Service Surveys', path: '/operator/service-surveys' },
      { text: 'Display Email Example', path: '/operator/display-email-example' },
      { text: 'Display SMS Example', path: '/operator/display-sms-example' },
    ],
  },
  {
    name: 'Welcome Texts',
    icon: 'fa fa-envelope',
    isOnlyMenu: true,
    path: '/operator/welcome-texts',
  },
  {
    name: 'Dashboard customization',
    icon: 'fa fa-cog',
    isOnlyMenu: true,
    path: '/operator/dashboard-customization',
  },
  {
    name: 'Statistics',
    icon: 'fa fa-line-chart',
    path: '/operator/service-metrics',
    children: [
      {
        name: 'Service Metrics',
        path: '/operator/service-metrics',
      },
      {
        name: 'Individual Statistics',
        path: '/operator/individual-statistics',
      },
      {
        name: 'Operation Metrics',
        path: '/operator/operation-metrics',
      },
      {
        name: 'Monthly Registration Retention',
        roles: ['admin', 'director', 'supervisor'],
        path: '/operator/monthly-registration-retention',
      },
      {
        name: 'Annual Registration Retention',
        roles: ['admin', 'director', 'supervisor'],
        path: '/operator/annual-registration-retention',
      },
      {
        name: 'Operator Registration Performances',
        path: '/operator/registration-performances',
      },
      {
        name: 'Operator Ride Performances',
        path: '/operator/ride-performances',
      },
    ],
    subItemList: [
      { text: 'Service Metrics', path: '/operator/service-metrics' },
      { text: 'Individual Statistics', path: '/operator/individual-statistics' },
      { text: 'Operation Metrics', path: '/operator/operation-metrics' },
      { text: 'A/B Tests', path: '/operator/ab-tests' },
      { text: 'Operator Registration Performances', path: '/operator/registration-performances' },
      { text: 'Operator Ride Performances', path: '/operator/ride-performances' },
    ],
  },
  {
    name: 'Forms',
    icon: 'fa fa-file-text-o',
    path: '/operator/user-forms',
  },
  {
    name: 'FAQ',
    icon: 'fa fa-question',
    path: '/operator/faq',
    description:
      'In this section, you can view what each color of the dashboard means. You can also view any frequently asked questions and our GoGo Acronyms.',
  },
  {
    name: 'Security',
    icon: 'fa fa-shield',
    path: '/operator/2fa',
    children: [
      {
        name: 'Two-Factor Authentication',
        path: '/operator/2fa',
      },
    ],
  },
];

export const affiliateWidgetList: SidebarItemType[] = [
  {
    name: 'Overview',
    icon: 'fa fa-dashboard',
    path: '/affiliate/overview',
  },
  {
    name: 'Security',
    icon: 'fa fa-shield',
    path: '/affiliate/2fa',
    children: [
      {
        name: 'Two-Factor Authentication',
        path: '/affiliate/2fa',
      },
    ],
  },
];

export const groceryPaths: string[] =
  operatorWidgetList.find((item: any) => item.name === 'Grocery')?.children?.map((child: any) => child.path) ?? [];
